import axios, { AxiosError } from 'axios';
import { useQuery, UseQueryOptions, QueryKey } from 'react-query';

import type { GreetResult, GreetFilters } from '~/types/api';
import type { DateFilterType } from '~/App/Contexts/DateContext';
import type { DefaultGetAllResponse } from '~/App/Api/rest';
import type { InteractionOptions } from '~/App/Contexts/InteractionContext';
import { keysToSnakeCase } from './util';

const queryKeys = {
  all: ['greet-results'] as const,
  lists: () => [...queryKeys.all, 'lists'] as const,
  list: (filters: GreetFiltersParams) =>
    [...queryKeys.lists(), filters] as const,
};

export { queryKeys as greetResultsQueryKeys };

export interface GreetFiltersParams {
  profileId?: string;
  assigneeId?: string;
  dateStart?: string;
  dateEnd?: string;
  dateFilter?: DateFilterType;
  interaction?: InteractionOptions;
  campaignId?: string;
}

export interface GreetResults
  extends Omit<DefaultGetAllResponse<GreetResult>, 'data'> {
  data?: GreetResult[];
}

export async function fetchResults(
  params: GreetFiltersParams
): Promise<GreetResults> {
  const res = await axios.get<DefaultGetAllResponse<GreetResult>>(
    '/api/v2/greets/results',
    {
      params: keysToSnakeCase(params) as GreetFilters,
    }
  );

  return res.data;
}

export type FetchPreviousGreetsParams = Pick<GreetFiltersParams, 'profileId'>;

export function useGreetResults(
  params: GreetFiltersParams,
  options?: UseQueryOptions<GreetResults, AxiosError>
) {
  return useQuery<GreetResults, AxiosError>(
    queryKeys.list(params) as QueryKey,
    () => fetchResults(params),
    options
  );
}

export function usePreviousGreets(
  { profileId }: FetchPreviousGreetsParams,
  options?: UseQueryOptions<GreetResults, AxiosError>
) {
  return useGreetResults(
    { profileId },
    {
      // default to not using error boundary since it's not a core API
      useErrorBoundary: false,
      ...options,
    }
  );
}
